// API Explorer endpoint catalog: one entry per developer-API route, including the
// metadata the UI renders (description + params, Postman-style). The `category`
// field drives the grouped left-menu (Documents / Sections / Glossary / Search).
const DOC_ID_PARAM = {
  name: 'id',
  in: 'path',
  required: true,
  description: 'The document id — use the doc_id values returned by Get Documents.',
  example: '01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK',
};

const SECTION_ID_PARAM = {
  name: 'id',
  in: 'path',
  required: true,
  description: 'The section (page) id — use the ids returned by Get Sections by ID.',
  example: 'bfd5f2d1-1f5b-4d0c-ae32-838a78589823',
};

const TITLE_PARAM = {
  name: 'title',
  in: 'query',
  required: true,
  description: 'Text to match — returns items whose title/name contains it (case-insensitive).',
  example: 'ASIC',
};

const QUERY_PARAM = {
  name: 'q',
  in: 'query',
  required: true,
  description: 'Search term — matches page titles and content across all documents.',
  example: 'takeover',
};

// The taxonomy keys. Rendered as a dropdown wherever a taxonomy is chosen.
const TAXONOMY_OPTIONS = ['regulator', 'regulation-category', 'entity-type', 'document-type'];

// Taxonomy key as a PATH segment (the Taxonomies endpoints).
const TAXONOMY_PATH_PARAM = {
  name: 'taxonomy',
  in: 'path',
  required: true,
  description: 'Taxonomy key — see GET /Taxonomies for the available keys.',
  example: 'regulator',
  options: TAXONOMY_OPTIONS,
};

// Taxonomy key as a QUERY param (Related Content by taxonomy item).
const TAXONOMY_QUERY_PARAM = {
  name: 'taxonomy',
  in: 'query',
  required: true,
  description: 'Taxonomy key — see GET /Taxonomies for the available keys.',
  example: 'regulator',
  options: TAXONOMY_OPTIONS,
};

// A taxonomy item id as a PATH segment (the Taxonomies endpoints).
const TAXONOMY_ITEM_ID_PATH_PARAM = {
  name: 'id',
  in: 'path',
  required: true,
  description: 'The id of an item within the taxonomy — from GET /Taxonomies/{taxonomy}/items.',
  example: '1',
};

// A taxonomy item id as a QUERY param (Related Content by taxonomy item).
const TAXONOMY_ITEM_ID_QUERY_PARAM = {
  name: 'itemId',
  in: 'query',
  required: true,
  description: 'The id of an item within the taxonomy — from GET /Taxonomies/{taxonomy}/items.',
  example: '3',
};

// Document id for the related-content-by-document endpoint. Uses a document that
// actually has related resources so "Try it" returns something.
const RELATED_CONTENT_DOC_ID_PARAM = {
  name: 'documentId',
  in: 'path',
  required: true,
  description:
    'The document id — returns the resources that apply to this document and its taxonomy items.',
  example: '01LVNLRUZN3WUDRKXTL5AKXXZBE3MQHED3',
};

// Example body for the AI query endpoint (a POST). Rendered in the Body tab and
// used as the editable default so the endpoint is one click away from a result.
const DOC_QUERY_BODY = {
  query:
    'What does this guide say about disclosure to investors?',
  documentIds: ['01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK'],
};

// Example body for the AI document-comparison endpoint (a POST). A real version pair:
// "Financial Accountability Regime Act 2023" V1 (older) → V2 (newer).
const COMPARE_BODY = {
  documentId1: '01LVNLRU53PMGI3ZRKNNEJJTFUY4RCICBM',
  documentId2: '01LVNLRU55AUFYTZU7R5CY57BOAK23OQAE',
  versionDate1: '2023-10-19T13:00:00Z',
  versionDate2: '2025-02-20T13:00:00Z',
};

// Display order of the menu groups.
export const CATEGORY_ORDER = ['Taxonomy', 'Documents', 'Sections', 'Glossary', 'Search', 'Related Content', 'AI'];

export const dummyData = [
  // ----- Documents -----
  {
    id: 0,
    title: 'Get Documents',
    endpoint: '/api/v1/Document/Get',
    pathTemplate: '/api/v1/Document/Get',
    request: 'GET',
    category: 'Documents',
    type: 'getDocuments',
    description: 'Lists every published regulatory document available to your account.',
    params: [],
  },
  {
    id: 1,
    title: 'Get Document by ID',
    endpoint: '/api/v1/Document/Get/01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK',
    pathTemplate: '/api/v1/Document/Get/{id}',
    request: 'GET',
    category: 'Documents',
    type: 'getDocByID',
    description: 'Returns a single document with its metadata.',
    params: [DOC_ID_PARAM],
  },
  {
    id: 8,
    title: 'Get Documents by Title',
    endpoint: '/api/v1/Document/GetByTitle?title=ASIC',
    pathTemplate: '/api/v1/Document/GetByTitle?title={title}',
    request: 'GET',
    category: 'Documents',
    type: 'getDocByTitle',
    description:
      'Finds documents whose title contains the given text (case-insensitive).',
    params: [TITLE_PARAM],
  },
  {
    id: 5,
    title: 'Get Related Documents by ID',
    endpoint:
      '/api/v1/Document/GetRelatedDocuments/01LVNLRU6LYBFOAO5I2NBIDLLNEXMD6DIA',
    pathTemplate: '/api/v1/Document/GetRelatedDocuments/{id}',
    request: 'GET',
    category: 'Documents',
    type: 'getRelatedDocumentsByDocID',
    description: 'Lists documents related to the given document.',
    params: [DOC_ID_PARAM],
  },
  {
    id: 7,
    title: 'Get Summary by Doc ID',
    endpoint: '/api/v1/Document/GetSummary/01LVNLRU55BFFEPXJI3FA3QFUEYTJLAGSS',
    pathTemplate: '/api/v1/Document/GetSummary/{id}',
    request: 'GET',
    category: 'AI',
    type: 'getSummaryByDocID',
    description: 'Returns the AI-generated summary for a document.',
    params: [DOC_ID_PARAM],
  },

  // ----- Sections -----
  {
    id: 2,
    title: 'Get Sections by Doc ID',
    endpoint: '/api/v1/Document/GetSections/01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK',
    pathTemplate: '/api/v1/Document/GetSections/{id}',
    request: 'GET',
    category: 'Sections',
    type: 'getSectionsByID',
    description: 'Lists the sections (pages) of a document.',
    params: [DOC_ID_PARAM],
  },
  {
    id: 4,
    title: 'Get Section by ID',
    endpoint: '/api/v1/Section/Get/bfd5f2d1-1f5b-4d0c-ae32-838a78589823',
    pathTemplate: '/api/v1/Section/Get/{id}',
    request: 'GET',
    category: 'Sections',
    type: 'getSectionByPageID',
    description: 'Returns a single section (page) with its content.',
    params: [SECTION_ID_PARAM],
  },
  {
    id: 9,
    title: 'Get Sections by Title',
    endpoint:
      '/api/v1/Section/GetByTitle?documentId=01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK&title=disclosure',
    pathTemplate: '/api/v1/Section/GetByTitle?documentId={documentId}&title={title}',
    request: 'GET',
    category: 'Sections',
    type: 'getSectionsByTitle',
    description:
      'Finds the sections of a document whose title contains the given text (case-insensitive). Scoped to one document.',
    params: [
      {
        name: 'documentId',
        in: 'query',
        required: true,
        description: 'The document to search within — use a doc_id from Get Documents.',
        example: '01LVNLRUZ2V4IOEIHYCJDIR3NRIVWLW3RK',
      },
      TITLE_PARAM,
    ],
  },
  {
    id: 6,
    title: 'Get Related Sections by ID',
    endpoint:
      '/api/v1/Section/GetRelatedSections/bfd5f2d1-1f5b-4d0c-ae32-838a78589823',
    pathTemplate: '/api/v1/Section/GetRelatedSections/{id}',
    request: 'GET',
    category: 'Sections',
    type: 'getRelatedSectionsByPageID',
    description: 'Lists sections related to the given section.',
    params: [SECTION_ID_PARAM],
  },

  // ----- Glossary -----
  {
    id: 3,
    title: 'Get Glossary by Doc ID',
    endpoint: '/api/v1/Document/GetGlossary/01LVNLRU22JU6N2LXRQJC23PCU4SDJCFFH',
    pathTemplate: '/api/v1/Document/GetGlossary/{id}',
    request: 'GET',
    category: 'Glossary',
    type: 'getGlossaryByDocId',
    description: 'Returns the glossary terms used in a document.',
    params: [DOC_ID_PARAM],
  },
  {
    id: 10,
    title: 'Get Glossary by Title',
    endpoint: '/api/v1/Document/GetGlossaryByTitle?title=ASIC',
    pathTemplate: '/api/v1/Document/GetGlossaryByTitle?title={title}',
    request: 'GET',
    category: 'Glossary',
    type: 'getGlossaryByTitle',
    description:
      'Finds glossary terms whose name contains the given text (case-insensitive).',
    params: [TITLE_PARAM],
  },

  // ----- Taxonomy ----- (the classification scheme: taxonomies → items → documents)
  {
    id: 17,
    title: 'Get Taxonomies',
    endpoint: '/api/v1/Taxonomies',
    pathTemplate: '/api/v1/Taxonomies',
    request: 'GET',
    category: 'Taxonomy',
    type: 'getTaxonomies',
    description:
      'Lists the taxonomies documents are classified by (Regulator, Regulation Category, Entity Type, Document Type) — each with its key, display name and item count. Start here, then use a taxonomy key with the endpoints below.',
    params: [],
  },
  {
    id: 12,
    title: 'Get Taxonomy Items',
    endpoint: '/api/v1/Taxonomies/regulator/items',
    pathTemplate: '/api/v1/Taxonomies/{taxonomy}/items',
    request: 'GET',
    category: 'Taxonomy',
    type: 'getTaxonomyItems',
    description:
      'Lists the items within a taxonomy (e.g. the regulators in “regulator”). Taxonomies are small, so the full set is returned.',
    params: [TAXONOMY_PATH_PARAM],
  },
  {
    id: 23,
    title: 'Get Taxonomy Item by ID',
    endpoint: '/api/v1/Taxonomies/regulator/items/1',
    pathTemplate: '/api/v1/Taxonomies/{taxonomy}/items/{id}',
    request: 'GET',
    category: 'Taxonomy',
    type: 'getTaxonomyItem',
    description:
      'Returns a single taxonomy item by its id — e.g. the regulator with id “1” (ASIC). Handy for resolving a stored id to its title.',
    params: [TAXONOMY_PATH_PARAM, TAXONOMY_ITEM_ID_PATH_PARAM],
  },
  {
    id: 15,
    title: 'Get Documents for Taxonomy Item',
    endpoint: '/api/v1/Taxonomies/regulator/items/1/documents',
    pathTemplate: '/api/v1/Taxonomies/{taxonomy}/items/{id}/documents',
    request: 'GET',
    category: 'Taxonomy',
    type: 'getTaxonomyItemDocuments',
    description:
      'Lists the documents tagged with a taxonomy item — e.g. every document under the regulator “ASIC”. Returns document summaries; fetch the full document with Get Document by ID.',
    params: [TAXONOMY_PATH_PARAM, TAXONOMY_ITEM_ID_PATH_PARAM],
  },

  // ----- Search -----
  {
    id: 11,
    title: 'Search content',
    endpoint: '/api/v1/Search/Get?q=takeover',
    pathTemplate: '/api/v1/Search/Get?q={q}',
    request: 'GET',
    category: 'Search',
    type: 'search',
    description:
      'Full-text search across all content — matches page titles and body text.',
    params: [QUERY_PARAM],
  },

  // ----- Related Content ----- (supplementary resources for a document or a taxonomy container)
  {
    id: 21,
    title: 'Get Related Content by Document',
    endpoint:
      '/api/v1/RelatedContent/GetByDocument/01LVNLRUZN3WUDRKXTL5AKXXZBE3MQHED3',
    pathTemplate: '/api/v1/RelatedContent/GetByDocument/{documentId}',
    request: 'GET',
    category: 'Related Content',
    type: 'getRelatedContentByDocument',
    description:
      'Lists the supplementary resources (PDFs, links) that apply to a document — those attached to the document itself, to any of its taxonomy items, and to the whole corpus. Each item reports where it applies (document, a taxonomy key like “regulator”, or global).',
    params: [RELATED_CONTENT_DOC_ID_PARAM],
  },
  {
    id: 22,
    title: 'Get Related Content by Taxonomy Item',
    endpoint: '/api/v1/RelatedContent/GetByTaxonomyItem?taxonomy=regulator&itemId=3',
    pathTemplate: '/api/v1/RelatedContent/GetByTaxonomyItem?taxonomy={taxonomy}&itemId={itemId}',
    request: 'GET',
    category: 'Related Content',
    type: 'getRelatedContentByTaxonomyItem',
    description:
      'Lists the supplementary resources pinned to a single taxonomy item (e.g. everything attached to the regulator “ASIC”). Use this to see the resources for a category regardless of any one document.',
    params: [TAXONOMY_QUERY_PARAM, TAXONOMY_ITEM_ID_QUERY_PARAM],
  },

  // ----- AI ----- (shown last in the menu via CATEGORY_ORDER; Get Summary is
  // also AI-generated and lives here too, though its category is set above.)
  {
    id: 18,
    title: 'Query a Document',
    endpoint: '/api/v1/Document/Query',
    pathTemplate: '/api/v1/Document/Query',
    request: 'POST',
    category: 'AI',
    type: 'queryDocuments',
    description:
      'Ask a natural-language question about one or more documents and get an AI-generated answer. Provide the question and at least one document id in the request body (Body tab).',
    params: [],
    body: DOC_QUERY_BODY,
  },
  {
    id: 19,
    title: 'Get My AI Queries',
    endpoint: '/api/v1/ApiQuery/my?take=20',
    pathTemplate: '/api/v1/ApiQuery/my?take={take}',
    request: 'GET',
    category: 'AI',
    type: 'getMyApiQueries',
    description:
      'Retrieve your most recent AI questions and answers (newest first). Scoped to your account and not counted against your call allowance.',
    params: [
      {
        name: 'take',
        in: 'query',
        description: 'How many recent queries to return (default 20, max 100).',
        example: '20',
      },
    ],
  },
  {
    id: 20,
    title: 'Compare Documents',
    endpoint: '/api/v1/Comparison/Compare',
    pathTemplate: '/api/v1/Comparison/Compare',
    request: 'POST',
    category: 'AI',
    type: 'compareDocuments',
    description:
      'AI-compare two versions of a document (V1 older, V2 newer) and get a plain-English summary of changes plus a section-by-section diff. Provide the two document ids and their version_date values in the request body (Body tab). Note: comparisons can take a minute or two to generate.',
    params: [],
    body: COMPARE_BODY,
  },
];

/**
 * Derives which API call a URL maps to, so hand-typed/edited URLs work and an
 * unknown URL fails gracefully. Order matters: the more specific routes (and the
 * query-param "…ByTitle"/Search routes, which have no trailing /id) must match
 * before the generic Get/{id} routes.
 */
export const resolveCallType = (url) => {
  const u = (url || '').trim();
  // Query-param routes (no trailing /id) first.
  if (/^\/api\/v\d+\/Document\/GetByTitle(\?|$)/i.test(u)) return 'getDocByTitle';
  if (/^\/api\/v\d+\/Document\/GetGlossaryByTitle(\?|$)/i.test(u)) return 'getGlossaryByTitle';
  if (/^\/api\/v\d+\/Section\/GetByTitle(\?|$)/i.test(u)) return 'getSectionsByTitle';
  if (/^\/api\/v\d+\/Search\/Get(\?|$)/i.test(u)) return 'search';
  // AI query — POST with a JSON body (matched before the generic Document routes).
  if (/^\/api\/v\d+\/Document\/Query(\?|$)/i.test(u)) return 'queryDocuments';
  if (/^\/api\/v\d+\/ApiQuery\/my(\?|$)/i.test(u)) return 'getMyApiQueries';
  if (/^\/api\/v\d+\/Comparison\/Compare(\?|$)/i.test(u)) return 'compareDocuments';
  if (/^\/api\/v\d+\/RelatedContent\/GetByTaxonomyItem(\?|$)/i.test(u)) return 'getRelatedContentByTaxonomyItem';
  if (/^\/api\/v\d+\/RelatedContent\/GetByDocument\/.+/i.test(u)) return 'getRelatedContentByDocument';
  // Taxonomy routes — most specific (item documents) first, bare /Taxonomies last.
  if (/^\/api\/v\d+\/Taxonomies\/[^/]+\/items\/[^/?]+\/documents(\?|$)/i.test(u)) return 'getTaxonomyItemDocuments';
  if (/^\/api\/v\d+\/Taxonomies\/[^/]+\/items\/[^/?]+/i.test(u)) return 'getTaxonomyItem';
  if (/^\/api\/v\d+\/Taxonomies\/[^/]+\/items(\?|$)/i.test(u)) return 'getTaxonomyItems';
  if (/^\/api\/v\d+\/Taxonomies\/?(\?|$)/i.test(u)) return 'getTaxonomies';
  // Path/id routes.
  if (/^\/api\/v\d+\/Document\/GetSections\/.+/i.test(u)) return 'getSectionsByID';
  if (/^\/api\/v\d+\/Document\/GetRelatedDocuments\/.+/i.test(u)) return 'getRelatedDocumentsByDocID';
  if (/^\/api\/v\d+\/Document\/GetGlossary\/.+/i.test(u)) return 'getGlossaryByDocId';
  if (/^\/api\/v\d+\/Document\/GetSummary\/.+/i.test(u)) return 'getSummaryByDocID';
  if (/^\/api\/v\d+\/Document\/Get\/.+/i.test(u)) return 'getDocByID';
  if (/^\/api\/v\d+\/Document\/Get\/?$/i.test(u)) return 'getDocuments';
  if (/^\/api\/v\d+\/Section\/GetRelatedSections\/.+/i.test(u)) return 'getRelatedSectionsByPageID';
  if (/^\/api\/v\d+\/Section\/Get\/.+/i.test(u)) return 'getSectionByPageID';
  return null;
};

/** The id segment after the endpoint prefix, e.g. /api/v1/Document/Get/{id}. */
export const idFromUrl = (url) => {
  const match = (url || '').match(/^\/api\/v\d+\/\w+\/\w+\/(.+)$/);
  return match ? match[1] : '';
};

/** The taxonomy key of a Taxonomies URL, e.g. /api/v1/Taxonomies/regulator/items → 'regulator'. */
export const taxonomyKeyFromUrl = (url) => {
  const match = (url || '').match(/^\/api\/v\d+\/Taxonomies\/([^/?]+)/i);
  return match ? decodeURIComponent(match[1]) : '';
};

/** The item id of a Taxonomies item URL, e.g. /Taxonomies/regulator/items/1[/documents] → '1'. */
export const taxonomyItemIdFromUrl = (url) => {
  const match = (url || '').match(/^\/api\/v\d+\/Taxonomies\/[^/]+\/items\/([^/?]+)/i);
  return match ? decodeURIComponent(match[1]) : '';
};

/** The value of a query-string param, e.g. ?title=ASIC → 'ASIC'. */
export const queryParamFromUrl = (url, name) => {
  const match = (url || '').match(new RegExp(`[?&]${name}=([^&]*)`, 'i'));
  return match ? decodeURIComponent(match[1]) : '';
};

/**
 * The value of a named PATH param, matched positionally against the endpoint's
 * template. Unlike idFromUrl (which grabs everything after the prefix), this
 * handles templates with several path params and trailing segments, e.g.
 * pathParamFromUrl('/api/v1/Taxonomies/regulator/items/1/documents',
 *   '/api/v1/Taxonomies/{taxonomy}/items/{id}/documents', 'id') → '1'.
 */
export const pathParamFromUrl = (url, pathTemplate, name) => {
  if (!url || !pathTemplate) return '';
  const tplSegs = pathTemplate.split('?')[0].split('/');
  const urlSegs = (url || '').split('?')[0].split('/');
  const idx = tplSegs.indexOf(`{${name}}`);
  if (idx === -1 || urlSegs[idx] == null) return '';
  return decodeURIComponent(urlSegs[idx]);
};

/** The catalog entry a URL corresponds to (by derived call type). */
export const endpointForUrl = (url) => {
  const type = resolveCallType(url);
  return type ? dummyData.find((item) => item.type === type) : null;
};
